import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Chip,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WhatsApp as WhatsAppIcon,
  School as SchoolIcon,
  CalendarViewWeek as CalendarViewWeekIcon,
  FormatListBulleted as FormatListBulletedIcon,
  CalendarMonth as CalendarMonthIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  NotificationsActive as NotificationsActiveIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';
import { sendWhatsAppClassScheduleReminder, openDirectWhatsAppWeb } from '../../services/WhatsAppService';
import './TeacherSchedules.css';

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// Helper function to format Class Name as "ClassName - SectionName"
export const formatClassName = (c) => {
  if (!c) return 'Class';
  const name = c.className || c.name || c.standard || '';
  const sec = c.sectionName || c.section || '';
  if (name && sec) return `${name} - ${sec}`;
  return name || sec || 'Class';
};

// Helper to extract full student name from any student object structure
const getStudentFullName = (s) => {
  if (!s) return '';
  if (typeof s === 'string') return s;

  const directName = s.displayName || s.name || s.fullName;
  if (directName) return directName;

  const directFirstLast = `${s.firstName || ''} ${s.lastName || ''}`.trim();
  if (directFirstLast) return directFirstLast;

  const userName = s.user?.displayName || `${s.user?.firstName || ''} ${s.user?.lastName || ''}`.trim() || s.user?.username;
  if (userName) return userName;

  const infoName = s.studentInfo?.displayName || `${s.studentInfo?.firstName || ''} ${s.studentInfo?.lastName || ''}`.trim();
  if (infoName) return infoName;

  return s.username || s.admissionNumber || s.admissionId || 'Student';
};

// Helper to extract student admission number
const getStudentAdmissionNo = (s) => {
  if (!s || typeof s !== 'object') return '';
  return s.admissionId || s.admissionNumber || s.studentInfo?.admissionId || s.user?.studentInfo?.admissionId || '';
};

// Helper to extract student phone number from any nested structure
const getStudentPhone = (s) => {
  if (!s) return '';
  if (typeof s === 'string') return '';
  return (
    s.phone || s.mobileNo || s.contactNo || s.mobile || s.phoneNumber ||
    s.studentInfo?.phone || s.studentInfo?.mobileNo || s.studentInfo?.contactNo || s.studentInfo?.mobile || s.studentInfo?.phoneNumber ||
    s.user?.phoneNumber || s.user?.phone || s.user?.mobileNo || s.user?.contactNo || s.user?.mobile ||
    s.user?.studentInfo?.phone || s.user?.studentInfo?.mobileNo || s.user?.studentInfo?.contactNo || s.user?.studentInfo?.phoneNumber ||
    s.contactInfo?.phone || s.contactInfo?.mobileNo || s.contactInfo?.contactNo || s.contactInfo?.phoneNumber || ''
  );
};

// Helper to format 24h time to 12h AM/PM (e.g. 5:00–6:00 PM)
const formatTimeRange = (startTime, endTime) => {
  if (!startTime) return 'N/A';
  const format12h = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${m < 10 ? '0' + m : m} ${period}`;
  };
  if (endTime) {
    const s12 = format12h(startTime).replace(/ AM| PM/, '');
    const e12 = format12h(endTime);
    return `${s12}–${e12}`;
  }
  return format12h(startTime);
};

// Helper to format duration (e.g. 1 hr, 1.5 hr)
const formatDuration = (durationMins) => {
  if (!durationMins) return '1 hr';
  if (durationMins === 60) return '1 hr';
  if (durationMins === 90) return '1.5 hr';
  if (durationMins === 120) return '2 hrs';
  if (durationMins < 60) return `${durationMins} mins`;
  const hrs = (durationMins / 60).toFixed(1).replace('.0', '');
  return `${hrs} hr${hrs === '1' ? '' : 's'}`;
};

const TeacherSchedules = ({ orgId: propOrgId }) => {
  const { axios, token, orgId: contextOrgId, user } = useAuth();
  const orgId = propOrgId || contextOrgId || user?.orgId;

  // View Mode: 'weekly' (default calendar grid), 'monthly', or 'list' (table)
  const [viewMode, setViewMode] = useState('weekly');
  const [currentMonthDate, setCurrentMonthDate] = useState(new Date());
  const [currentWeekDate, setCurrentWeekDate] = useState(new Date());

  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [activeSchedule, setActiveSchedule] = useState(null);
  const [loading, setLoading] = useState(false);

  // Auto 1-Hr WhatsApp Reminder Toggle
  const [autoReminderOn, setAutoReminderOn] = useState(() => {
    try { return sessionStorage.getItem('autoScheduleReminder') === 'true'; } catch { return false; }
  });
  const sentRemindersRef = useRef(new Set()); // tracks sent reminder keys to avoid duplicates
  const reminderIntervalRef = useRef(null);

  // Dynamic Organization Name from Org API
  const [orgDisplayName, setOrgDisplayName] = useState(() => {
    return user?.orgDetails?.orgName ||
      (typeof window !== 'undefined' && orgId ? localStorage.getItem(`org_name_${orgId}`) : '') ||
      '';
  });

  const orgDisplayNameRef = useRef(orgDisplayName);
  useEffect(() => {
    orgDisplayNameRef.current = orgDisplayName;
  }, [orgDisplayName]);

  // Fetch true Organization Name from Org API
  useEffect(() => {
    if (!orgId || !axios) return;
    const fetchOrgName = async () => {
      try {
        const orgUrl = configData.get_identity_identity_org_orgid
          ? configData.get_identity_identity_org_orgid(orgId, orgId)
          : configData.get_identity_identity_org
          ? configData.get_identity_identity_org(orgId)
          : `${configData.GET_ORG_BY_ID}/${orgId}`;

        const res = await axios.get(orgUrl, {
          headers: { accept: 'application/hal+json' }
        });
        const name = res.data?.orgName || res.data?.name || res.data?.displayName || res.data?.organizationName;
        if (name && !/^[0-9a-fA-F]{8}-/.test(name.trim())) {
          const clean = name.trim();
          setOrgDisplayName(clean);
          try {
            localStorage.setItem(`org_name_${orgId}`, clean);
          } catch (e) {}
        }
      } catch (err) {
        console.warn('Could not fetch org name from Org API in TeacherSchedules:', err);
      }
    };
    fetchOrgName();
  }, [orgId, axios]);

  // Refs to hold latest values WITHOUT causing reminder effect to re-run
  const activeScheduleRef = useRef(null);
  const orgIdRef = useRef(orgId);
  const tokenRef = useRef(token);
  const userRef = useRef(user);

  // Add / Edit Entry Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntryIndex, setEditingEntryIndex] = useState(null);
  const [entryForm, setEntryForm] = useState({
    days: ["Monday"],
    startTime: '17:00',
    endTime: '18:00',
    durationMins: 60,
    studentIds: [],
    classId: '',
    course: '',
    location: 'Studio A'
  });

  // Popup Detail View Modal State
  const [selectedEntryDetail, setSelectedEntryDetail] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const [classStudents, setClassStudents] = useState([]);

  // Handle Class Selection in Modal
  const handleClassChangeInModal = async (selectedClassId) => {
    setEntryForm(prev => ({ ...prev, classId: selectedClassId, studentIds: [] }));
    if (!selectedClassId) {
      setClassStudents([]);
      return;
    }

    try {
      const res = await axios.get(`${configData.GET_STUDENTS_BY_CLASS}/${selectedClassId}`, {
        headers: { Authorization: token }
      });
      const list = res.data?.items || res.data?.content || (Array.isArray(res.data) ? res.data : []);
      setClassStudents(Array.isArray(list) ? list : []);
    } catch (err) {
      console.warn("Failed to fetch class students:", err);
      setClassStudents([]);
    }
  };

  // Fetch Metadata (Classes & Students)
  const fetchMetadata = useCallback(async () => {
    if (!orgId) return;
    try {
      // Fetch Classes
      try {
        const classRes = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`, {
          headers: { Authorization: token }
        });
        setClasses(classRes.data?.items || classRes.data || []);
      } catch (e1) {
        const classRes2 = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}`, {
          headers: { Authorization: token }
        });
        setClasses(classRes2.data?.items || classRes2.data || []);
      }

      // Fetch Students
      try {
        const stRes = await axios.get(`${configData.GET_USERS_BY_TYPE}/${orgId}`, {
          params: { userType: 'STUDENT', pageSize: 200 },
          headers: { Authorization: token }
        });
        setStudents(stRes.data?.items || stRes.data?.content || stRes.data || []);
      } catch (e2) {
        const stRes2 = await axios.get(`${configData.SEARCH_USERS}?orgId=${orgId}&role=STUDENT`, {
          headers: { Authorization: token }
        });
        setStudents(stRes2.data?.items || stRes2.data || []);
      }
    } catch (err) {
      console.error("Fetch metadata error:", err);
    }
  }, [axios, orgId, token]);

  // Fetch Schedules
  const fetchSchedules = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      const facultyId = user?.userId || user?.id || user?.user?.userId || 'default_teacher';

      let items = [];
      try {
        const res = await axios.get(`${configData.SCHEDULES}`, {
          params: { filterType: 'faculty', id: facultyId, orgId, pageSize: 50 },
          headers: { Authorization: token }
        });
        items = res.data?.items || res.data?.content || (Array.isArray(res.data) ? res.data : []);
      } catch (e1) {
        console.warn("Fetch schedules error:", e1);
      }

      if (items.length > 0) {
        setSchedules(items);
        setActiveSchedule(items[0]);
      }
    } catch (err) {
      console.error("Fetch schedules error:", err);
    } finally {
      setLoading(false);
    }
  }, [axios, orgId, token, user]);

  useEffect(() => {
    fetchMetadata();
    fetchSchedules();
  }, [fetchMetadata, fetchSchedules]);

  // Keep refs in sync with latest state/props — NO extra renders, NO API calls
  useEffect(() => { activeScheduleRef.current = activeSchedule; }, [activeSchedule]);
  useEffect(() => { orgIdRef.current = orgId; }, [orgId]);
  useEffect(() => { tokenRef.current = token; }, [token]);
  useEffect(() => { userRef.current = user; }, [user]);

  // ── Auto 1-Hr Reminder Engine ──────────────────────────────────────
  // Only depends on [autoReminderOn] — does NOT re-run on data/schedule changes
  useEffect(() => {
    try { sessionStorage.setItem('autoScheduleReminder', autoReminderOn ? 'true' : 'false'); } catch { }

    if (reminderIntervalRef.current) {
      clearInterval(reminderIntervalRef.current);
      reminderIntervalRef.current = null;
    }

    if (!autoReminderOn) return;

    const checkAndSendReminders = async () => {
      const schedule = activeScheduleRef.current;
      const currentOrgId = orgIdRef.current;
      const currentToken = tokenRef.current;
      const currentUser = userRef.current;

      if (!schedule || !schedule.scheduleItems) return;
      const now = new Date();

      // Check today's and tomorrow's entries
      for (const when of ['today', 'tomorrow']) {
        const checkDate = new Date(now);
        if (when === 'tomorrow') checkDate.setDate(checkDate.getDate() + 1);
        const checkDayName = checkDate.toLocaleDateString('en-US', { weekday: 'long' });
        const checkDateStr = checkDate.toLocaleDateString('en-CA');

        const dayKey = checkDayName.toUpperCase();
        const items = schedule.scheduleItems[dayKey] ||
          schedule.scheduleItems[checkDayName] ||
          schedule.scheduleItems[checkDayName.toLowerCase()] || [];

        for (const evt of items) {
          if (!evt.startTime) continue;
          const [h, m] = evt.startTime.split(':').map(Number);
          const classStart = new Date(checkDate);
          classStart.setHours(h, m, 0, 0);

          const diffMins = (classStart - now) / 60000;

          // Send only if class is 55–65 minutes away
          if (diffMins >= 55 && diffMins <= 65) {
            const reminderKey = `${checkDateStr}-${evt.startTime}-${evt.studentName || 'student'}`;
            if (sentRemindersRef.current.has(reminderKey)) continue;

            sentRemindersRef.current.add(reminderKey);
            const dateTimeFormatted = `${checkDayName}, ${checkDateStr} at ${evt.startTime} (1-Hr Auto Reminder)`;

            try {
              const activeOrgName = orgDisplayNameRef.current || currentUser?.orgDetails?.orgName || (currentOrgId ? localStorage.getItem(`org_name_${currentOrgId}`) : '') || '';
              await sendWhatsAppClassScheduleReminder({
                orgId: currentOrgId,
                orgName: activeOrgName,
                phoneNumber: evt.phoneNumber || evt.studentPhone || '',
                recipientName: evt.studentName || 'Student',
                courseOrSubject: evt.course || 'Class Subject',
                className: evt.className || evt.studentName || 'Class',
                dateTime: dateTimeFormatted,
                token: currentToken
              });
              toast.success(`🔔 Auto-reminder sent to ${evt.studentName || 'Student'} for ${evt.course || 'class'} at ${evt.startTime}!`, { autoClose: 4000 });
            } catch (err) {
              console.error('Auto-reminder send failed:', err);
            }
          }
        }
      }
    };

    // Run immediately, then every 60 seconds
    checkAndSendReminders();
    reminderIntervalRef.current = setInterval(checkAndSendReminders, 60000);

    return () => {
      if (reminderIntervalRef.current) clearInterval(reminderIntervalRef.current);
    };
  }, [autoReminderOn]); // ← Only re-runs when toggle changes — NO API re-renders!
  // ──────────────────────────────────────────────────────────────────

  // Flatten Schedule Items into Array with day grouping
  const getFlatEntries = () => {
    if (!activeSchedule || !activeSchedule.scheduleItems) return [];
    const flat = [];
    DAYS.forEach(day => {
      const dayUpper = day.toUpperCase();
      const dayLower = day.toLowerCase();
      const dayTitle = day.charAt(0).toUpperCase() + day.slice(1).toLowerCase();

      const items = activeSchedule.scheduleItems[dayUpper] ||
        activeSchedule.scheduleItems[day] ||
        activeSchedule.scheduleItems[dayLower] ||
        activeSchedule.scheduleItems[dayTitle] || [];

      items.forEach((item, idx) => {
        let resolvedStudentName = item.studentName || item.studentNames;

        if (!resolvedStudentName || resolvedStudentName === 'Student' || resolvedStudentName === 'All Students') {
          if (item.studentIds && item.studentIds.length > 0) {
            const names = item.studentIds.map(sid => {
              const matchedSt = (students || []).find(s => String(s?.userId || s?.id || s?.user?.userId || s?.user?.id) === String(sid)) ||
                (classStudents || []).find(s => String(s?.userId || s?.id || s?.user?.userId || s?.user?.id) === String(sid));
              return matchedSt ? getStudentFullName(matchedSt) : sid;
            });
            resolvedStudentName = names.join(', ');
          }
        }

        if (!resolvedStudentName) {
          if (activeSchedule.classId) {
            const cObj = (classes || []).find(c => String(c?.classId || c?.id || c?.value) === String(activeSchedule.classId));
            resolvedStudentName = cObj ? formatClassName(cObj) : 'All Class Students';
          } else {
            resolvedStudentName = 'All Students';
          }
        }

        // Resolve phone number from studentIds match
        let resolvedPhone = item.phoneNumber || item.studentPhone || item.phone || '';
        if (!resolvedPhone && item.studentIds && item.studentIds.length > 0) {
          const firstSid = item.studentIds[0];
          const matchedSt = (students || []).find(s => String(s?.userId || s?.id || s?.user?.userId || s?.user?.id) === String(firstSid)) ||
            (classStudents || []).find(s => String(s?.userId || s?.id || s?.user?.userId || s?.user?.id) === String(firstSid));
          if (matchedSt) resolvedPhone = getStudentPhone(matchedSt);
        }

        flat.push({
          day,
          dayUpper,
          itemIndexInDay: idx,
          startTime: item.startTime,
          endTime: item.endTime,
          durationMins: item.durationMins || 60,
          studentName: resolvedStudentName,
          course: item.subject || item.course || 'Class',
          location: item.location || 'Studio A',
          phoneNumber: resolvedPhone,
          rawItem: item
        });
      });
    });
    return flat;
  };

  const entries = getFlatEntries();

  // Group entries by Day for Weekly & Monthly Grid View
  const entriesByDay = {};
  DAYS.forEach(day => {
    entriesByDay[day] = entries.filter(e => e.day === day);
  });

  const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const handlePrevMonth = () => {
    setCurrentMonthDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonthDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleTodayMonth = () => {
    setCurrentMonthDate(new Date());
  };

  // Generate calendar grid cells for Monthly View (repeating weekly schedule across academic year)
  const getMonthCalendarCells = () => {
    const year = currentMonthDate.getFullYear();
    const month = currentMonthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const dayOfWeek = firstDay.getDay(); // 0 = Sun, 1 = Mon ...
    const offset = (dayOfWeek + 6) % 7; // Mon = 0, Tue = 1 ... Sun = 6
    const startDate = new Date(year, month, 1 - offset);

    const cells = [];
    let curr = new Date(startDate);

    for (let i = 0; i < 35; i++) {
      const isCurrentMonth = curr.getMonth() === month;
      const dayNum = curr.getDate();
      const dayName = curr.toLocaleDateString('en-US', { weekday: 'long' });
      const fullDateStr = curr.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      const dayEvents = entriesByDay[dayName] || [];

      cells.push({
        dateObj: new Date(curr),
        dayNum,
        dayName,
        fullDateStr,
        isCurrentMonth,
        isToday: curr.toDateString() === new Date().toDateString(),
        events: dayEvents
      });
      curr.setDate(curr.getDate() + 1);
    }

    if (curr.getMonth() === month) {
      for (let i = 0; i < 7; i++) {
        const isCurrentMonth = curr.getMonth() === month;
        const dayNum = curr.getDate();
        const dayName = curr.toLocaleDateString('en-US', { weekday: 'long' });
        const fullDateStr = curr.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        const dayEvents = entriesByDay[dayName] || [];

        cells.push({
          dateObj: new Date(curr),
          dayNum,
          dayName,
          fullDateStr,
          isCurrentMonth,
          isToday: curr.toDateString() === new Date().toDateString(),
          events: dayEvents
        });
        curr.setDate(curr.getDate() + 1);
      }
    }

    return cells;
  };

  // Week Navigation & Dates Logic for Weekly Grid
  const getMondayOfWeek = (d) => {
    const date = new Date(d);
    const day = date.getDay(); // 0=Sun, 1=Mon...
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(date.setDate(diff));
  };

  const currentMonday = getMondayOfWeek(currentWeekDate);
  const currentSunday = new Date(currentMonday);
  currentSunday.setDate(currentMonday.getDate() + 6);

  const handlePrevWeek = () => {
    setCurrentWeekDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 7);
      return d;
    });
  };

  const handleNextWeek = () => {
    setCurrentWeekDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 7);
      return d;
    });
  };

  const handleTodayWeek = () => {
    setCurrentWeekDate(new Date());
  };

  const getWeekDaysWithDates = () => {
    return DAYS.map((dayName, idx) => {
      const dateObj = new Date(currentMonday);
      dateObj.setDate(currentMonday.getDate() + idx);
      const dayNum = dateObj.getDate();
      const monthShort = dateObj.toLocaleDateString('en-US', { month: 'short' });
      const formattedDateStr = `${dayNum} ${monthShort}`;
      const fullDateStr = dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      const isToday = dateObj.toDateString() === new Date().toDateString();

      return {
        dayName,
        dateObj,
        dayNum,
        monthShort,
        formattedDateStr,
        fullDateStr,
        isToday,
        events: entriesByDay[dayName] || []
      };
    });
  };

  // Open Popup Detail View Modal
  const handleOpenDetailModal = (entry, flatIdx, specificDate = null) => {
    setSelectedEntryDetail({ ...entry, flatIndex: flatIdx, specificDate });
    setDetailModalOpen(true);
  };

  // Open Modal for New Entry or Edit Entry
  const handleOpenAddModal = (dayPreset = null, entryToEdit = null, flatIndex = null) => {
    if (entryToEdit) {
      setEditingEntryIndex(flatIndex);
      const raw = entryToEdit.rawItem || {};
      setEntryForm({
        days: [entryToEdit.day],
        startTime: entryToEdit.startTime || '17:00',
        endTime: entryToEdit.endTime || '18:00',
        durationMins: entryToEdit.durationMins || 60,
        studentIds: raw.studentIds || [],
        classId: raw.classId || '',
        course: entryToEdit.course || '',
        location: raw.location || 'Studio A'
      });
      if (raw.classId) {
        handleClassChangeInModal(raw.classId);
      }
    } else {
      setEditingEntryIndex(null);
      setEntryForm({
        days: dayPreset ? [dayPreset] : ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        startTime: '17:00',
        endTime: '18:00',
        durationMins: 60,
        studentIds: [],
        classId: '',
        course: '',
        location: 'Studio A'
      });
    }
    setModalOpen(true);
  };

  // Save Schedule Entry
  const handleSaveEntry = async () => {
    if (!entryForm.days || entryForm.days.length === 0) {
      toast.warning("Please select at least one day for the schedule.");
      return;
    }
    if (!entryForm.course.trim()) {
      toast.warning("Please enter a Course name (e.g. Classical Dance, Guitar).");
      return;
    }

    try {
      const currentItemsMap = activeSchedule?.scheduleItems ? { ...activeSchedule.scheduleItems } : {};

      DAYS.forEach(d => {
        const dU = d.toUpperCase();
        if (!currentItemsMap[dU]) currentItemsMap[dU] = [];
      });

      // Calculate duration
      let duration = entryForm.durationMins;
      if (entryForm.startTime && entryForm.endTime) {
        const [sH, sM] = entryForm.startTime.split(':').map(Number);
        const [eH, eM] = entryForm.endTime.split(':').map(Number);
        const diff = (eH * 60 + eM) - (sH * 60 + sM);
        if (diff > 0) duration = diff;
      }

      const resolvedStudentName = (entryForm.studentIds || []).map(sid => {
        const stList = Array.isArray(classStudents) && classStudents.length > 0 ? classStudents : (students || []);
        const st = stList.find(s => String(s?.userId || s?.id || s?.user?.userId || s?.user?.id) === String(sid));
        return st ? getStudentFullName(st) : sid;
      }).join(', ') || (entryForm.classId ? formatClassName((classes || []).find(c => String(c?.classId || c?.id || c?.value) === String(entryForm.classId))) : 'All Students');

      // If editing a single item, remove old slot first
      if (editingEntryIndex !== null && entries[editingEntryIndex]) {
        const prevEntry = entries[editingEntryIndex];
        const prevDayUpper = prevEntry.dayUpper;
        currentItemsMap[prevDayUpper].splice(prevEntry.itemIndexInDay, 1);
      }

      // Add slot for each selected day
      entryForm.days.forEach(dayName => {
        const dayUpper = dayName.toUpperCase();
        if (!currentItemsMap[dayUpper]) currentItemsMap[dayUpper] = [];

        const newItem = {
          itemType: 'LECTURE',
          subject: entryForm.course,
          studentIds: entryForm.studentIds,
          studentName: resolvedStudentName,
          classId: entryForm.classId || null,
          startTime: entryForm.startTime,
          endTime: entryForm.endTime,
          durationMins: duration,
          location: entryForm.location || 'Studio A'
        };

        currentItemsMap[dayUpper].push(newItem);
      });

      const facultyId = user?.userId || user?.id || 'default_teacher';
      const payload = {
        scheduleId: activeSchedule?.scheduleId || null,
        orgId,
        facultyId,
        classId: entryForm.classId || null,
        academicYear: '2026-2027',
        scheduleType: 'TIMETABLE',
        scheduleName: `${user?.displayName || 'User'}'s Schedule`,
        effectiveFrom: new Date('2026-09-01T00:00:00Z').toISOString(),
        effectiveTo: new Date('2027-03-31T00:00:00Z').toISOString(),
        scheduleItems: currentItemsMap
      };

      let savedSchedule = null;
      try {
        if (activeSchedule?.scheduleId) {
          const res = await axios.put(`${configData.SCHEDULES}/${activeSchedule.scheduleId}`, payload, {
            headers: { Authorization: token, 'Content-Type': 'application/json' }
          });
          savedSchedule = res.data?.item || res.data || payload;
        } else {
          const res = await axios.post(`${configData.SCHEDULES}`, payload, {
            headers: { Authorization: token, 'Content-Type': 'application/json' }
          });
          savedSchedule = res.data?.item || res.data || { ...payload, scheduleId: `sch_${Date.now()}` };
        }
      } catch (apiErr) {
        console.warn("Backend schedule save error, persisting in local state:", apiErr);
        savedSchedule = { ...payload, scheduleId: activeSchedule?.scheduleId || `sch_${Date.now()}` };
      }

      // Instantly update active schedule state so entries render immediately
      if (savedSchedule) {
        setActiveSchedule(savedSchedule);
        setSchedules(prev => {
          const existingIdx = prev.findIndex(s => s.scheduleId === savedSchedule.scheduleId);
          if (existingIdx >= 0) {
            const copy = [...prev];
            copy[existingIdx] = savedSchedule;
            return copy;
          }
          return [savedSchedule, ...prev];
        });
      }

      toast.success(`Schedule saved for ${entryForm.days.length} day(s)!`);
      setModalOpen(false);
    } catch (err) {
      console.error("Save entry error:", err);
      toast.error("Failed to save entry.");
    }
  };

  // Delete Single Entry
  const handleDeleteEntry = async (entryIndex) => {
    if (!activeSchedule) return;
    const target = entries[entryIndex];
    if (!target) return;

    try {
      const currentItemsMap = { ...activeSchedule.scheduleItems };
      currentItemsMap[target.dayUpper].splice(target.itemIndexInDay, 1);

      const payload = {
        ...activeSchedule,
        scheduleItems: currentItemsMap
      };

      const res = await axios.put(`${configData.SCHEDULES}/${activeSchedule.scheduleId}`, payload, {
        headers: { Authorization: token, 'Content-Type': 'application/json' }
      });

      const updated = res.data || payload;
      setActiveSchedule(updated);

      toast.success("Entry deleted!");
      fetchSchedules();
    } catch (err) {
      console.error("Delete entry error:", err);
      toast.error("Failed to delete entry.");
    }
  };

  // WhatsApp Share
  const handleWhatsAppShare = async (row) => {
    if (!row) return;

    // Comprehensive phone number resolution
    let phoneNumber =
      row.phoneNumber || row.studentPhone || row.phone ||
      row.rawItem?.phoneNumber || row.rawItem?.phone || row.rawItem?.studentPhone || '';

    // If still no phone, search the students list by name
    if (!phoneNumber && row.studentName) {
      const matchedSt = (students || []).find(s => getStudentFullName(s) === row.studentName);
      if (matchedSt) phoneNumber = getStudentPhone(matchedSt);
    }

    // If still no phone, try matching by studentId from rawItem
    if (!phoneNumber && row.rawItem?.studentIds?.length > 0) {
      const sid = row.rawItem.studentIds[0];
      const matchedSt = (students || []).find(s =>
        String(s?.userId || s?.id || s?.user?.userId || s?.user?.id) === String(sid)
      );
      if (matchedSt) phoneNumber = getStudentPhone(matchedSt);
    }

    const dateDisplay = row.specificDate ? `${row.day}, ${row.specificDate}` : (row.day || 'Upcoming');
    const timeDisplay = formatTimeRange(row.startTime, row.endTime);
    const orgName = orgDisplayName || user?.orgDetails?.orgName || (orgId ? localStorage.getItem(`org_name_${orgId}`) : '') || '';

    if (!phoneNumber) {
      toast.warn(`No phone number found for ${row.studentName || 'this student'}. Please update student contact info.`);
      return;
    }

    try {
      // Try API template first (same as fee payments)
      await sendWhatsAppClassScheduleReminder({
        orgId,
        orgName,
        phoneNumber,
        recipientName: row.studentName || 'Student',
        courseOrSubject: row.course || 'Class Subject',
        className: row.className || row.studentName || 'Class',
        dateTime: `${dateDisplay} at ${timeDisplay}`,
        token
      });
      toast.success(`✅ WhatsApp message sent to ${row.studentName || 'Student'} (${phoneNumber})!`);
    } catch (err) {
      // Fallback: open WhatsApp Web directly to student's number
      console.warn('API send failed, falling back to WhatsApp Web:', err);
      const orgSignature = orgName ? `\n\nRegards,\n${orgName} Team` : '';
      const msgText =
        `💃 *Class Schedule Reminder* 🎵\n\n` +
        `Hi ${row.studentName || 'Student'},\n\n` +
        `📅 *Date:* ${dateDisplay}\n` +
        `⏰ *Time:* ${timeDisplay}\n` +
        `⏱️ *Duration:* ${formatDuration(row.durationMins)}\n` +
        `📚 *Course:* ${row.course || 'Class'}\n` +
        `📍 *Location:* ${row.location || 'Studio A'}\n\n` +
        `Please arrive 5 minutes before class time!${orgSignature}`;
      openDirectWhatsAppWeb(phoneNumber, msgText);
      toast.info(`Opening WhatsApp for ${row.studentName || 'Student'}...`);
    }
  };

  // Send 1-Hour Before Class Schedule WhatsApp Reminder via Meta Template CLASS_SCHEDULE_REMINDER
  const handleSend1HrScheduleReminder = async (row) => {
    if (!row) return;

    // Deep phone lookup — same as handleWhatsAppShare
    let phoneNumber =
      row.phoneNumber || row.studentPhone || row.phone ||
      row.rawItem?.phoneNumber || row.rawItem?.phone || row.rawItem?.studentPhone || '';

    if (!phoneNumber && row.studentName) {
      const matchedSt = (students || []).find(s => getStudentFullName(s) === row.studentName);
      if (matchedSt) phoneNumber = getStudentPhone(matchedSt);
    }
    if (!phoneNumber && row.rawItem?.studentIds?.length > 0) {
      const sid = row.rawItem.studentIds[0];
      const matchedSt = (students || []).find(s =>
        String(s?.userId || s?.id || s?.user?.userId || s?.user?.id) === String(sid)
      );
      if (matchedSt) phoneNumber = getStudentPhone(matchedSt);
    }

    const dateStr = row.specificDate ? `${row.day}, ${row.specificDate}` : row.day;
    const dateTimeFormatted = `${dateStr} at ${formatTimeRange(row.startTime, row.endTime)} (1-Hr Reminder)`;
    const orgName = orgDisplayName || user?.orgDetails?.orgName || (orgId ? localStorage.getItem(`org_name_${orgId}`) : '') || '';

    try {
      await sendWhatsAppClassScheduleReminder({
        orgId,
        orgName,
        phoneNumber,
        recipientName: row.studentName || 'Student',
        courseOrSubject: row.course || 'Class Subject',
        className: row.className || row.studentName || 'Class',
        dateTime: dateTimeFormatted,
        token
      });
      toast.success(`🔔 1-Hour WhatsApp Reminder sent to ${row.studentName || 'Student'} for ${row.course || 'Class'}!`);
    } catch (err) {
      console.error("WhatsApp Class Schedule Reminder Error:", err);
      toast.error("Failed to send 1-hour WhatsApp reminder.");
    }

  };

  return (
    <Box className="schedules-container">
      {/* Top Header */}
      <Box className="schedules-header">
        <Box className="schedules-title-section">
          <Typography variant="h5" style={{ fontWeight: 800, color: '#332822', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <SchoolIcon style={{ color: '#014486', fontSize: '28px' }} />
            Schedules
          </Typography>
          <Typography variant="body2" style={{ color: '#78685d', marginTop: '4px' }}>
            Manage class times, courses, and student schedules.
          </Typography>
        </Box>

        <Box style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          {/* View Switcher Toggle Bar */}
          <Box className="view-toggle-bar">
            <Button
              className={`view-btn ${viewMode === 'weekly' ? 'active' : ''}`}
              startIcon={<CalendarViewWeekIcon />}
              onClick={() => setViewMode('weekly')}
            >
              Weekly Grid
            </Button>
            <Button
              className={`view-btn ${viewMode === 'monthly' ? 'active' : ''}`}
              startIcon={<CalendarMonthIcon />}
              onClick={() => setViewMode('monthly')}
            >
              Monthly View
            </Button>
            <Button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              startIcon={<FormatListBulletedIcon />}
              onClick={() => setViewMode('list')}
            >
              Table View
            </Button>
          </Box>

          {/* Auto 1-Hr Reminder Toggle — MUI Switch */}
          <Tooltip title={autoReminderOn ? 'Auto reminders ON — click to turn OFF' : 'Auto reminders OFF — click to turn ON'}>
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: autoReminderOn ? '#f0f7ff' : '#f9f6f2',
                border: `1.5px solid ${autoReminderOn ? '#014486' : '#eedfd0'}`,
                borderRadius: '12px',
                padding: '6px 14px 6px 10px',
                transition: 'all 0.25s ease',
                boxShadow: autoReminderOn ? '0 0 10px rgba(1,68,134,0.18)' : 'none',
                cursor: 'pointer'
              }}
              onClick={() => {
                const next = !autoReminderOn;
                setAutoReminderOn(next);
                toast.info(next ? '🔔 Auto 1-Hr Reminders turned ON' : '🔕 Auto 1-Hr Reminders turned OFF', { autoClose: 2500 });
              }}
            >
              <NotificationsActiveIcon style={{ fontSize: '16px', color: autoReminderOn ? '#014486' : '#a39285' }} />
              <Box>
                <Typography style={{ fontSize: '11px', fontWeight: 800, color: autoReminderOn ? '#014486' : '#5c4d43', lineHeight: 1.1 }}>
                  Auto Reminder
                </Typography>
                <Typography style={{ fontSize: '10px', color: autoReminderOn ? '#014486' : '#a39285', lineHeight: 1.2 }}>
                  1 hr before class
                </Typography>
              </Box>
              <Switch
                checked={autoReminderOn}
                onChange={() => {}}
                size="small"
                onClick={e => e.stopPropagation()}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#014486' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#014486' },
                  pointerEvents: 'none'
                }}
              />
            </Box>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenAddModal()}
            style={{ background: '#014486', textTransform: 'none', fontWeight: 700, borderRadius: '10px', padding: '10px 22px' }}
          >
            Add Schedule Entry
          </Button>
        </Box>
      </Box>

      {/* VIEW 1: WEEKLY CALENDAR GRID VIEW (DEFAULT) */}
      {viewMode === 'weekly' && (
        <Box>
          {/* Week Navigation Header */}
          <Box className="weekly-nav-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', background: '#ffffff', padding: '12px 18px', borderRadius: '12px', border: '1px solid #eedfd0' }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <IconButton onClick={handlePrevWeek} size="small" style={{ border: '1px solid #eedfd0', background: '#fffcf9' }}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="h6" style={{ fontWeight: 800, color: '#332822', minWidth: '220px', textAlign: 'center', fontSize: '16px' }}>
                {currentMonday.getDate()} {currentMonday.toLocaleDateString('en-US', { month: 'short' })} – {currentSunday.getDate()} {currentSunday.toLocaleDateString('en-US', { month: 'short' })} {currentSunday.getFullYear()}
              </Typography>
              <IconButton onClick={handleNextWeek} size="small" style={{ border: '1px solid #eedfd0', background: '#fffcf9' }}>
                <ChevronRightIcon />
              </IconButton>
              <Button
                size="small"
                variant="outlined"
                onClick={handleTodayWeek}
                style={{ textTransform: 'none', fontWeight: 700, borderColor: '#014486', color: '#014486', borderRadius: '8px' }}
              >
                Today
              </Button>
            </Box>
            <Typography variant="body2" style={{ color: '#78685d', fontWeight: 600 }}>
              📅 Week of {currentMonday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {currentSunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </Typography>
          </Box>

          <Box className="weekly-calendar-grid">
            {getWeekDaysWithDates().map(dayInfo => {
              const dayEvents = dayInfo.events;
              return (
                <Box key={dayInfo.dayName} className="calendar-day-column">
                  <Box className={`calendar-day-header ${dayInfo.isToday ? 'today' : ''}`}>
                    <Box>
                      <span className="calendar-day-name">{dayInfo.dayName}</span>
                      <div className="calendar-day-date-str" style={{ fontSize: '11px', fontWeight: 800, color: dayInfo.isToday ? '#014486' : '#78685d', marginTop: '2px' }}>
                        {dayInfo.formattedDateStr}
                      </div>
                    </Box>
                    <span className="calendar-day-count">{dayEvents.length} {dayEvents.length === 1 ? 'class' : 'classes'}</span>
                  </Box>

                  <Box className="calendar-day-body">
                    {dayEvents.length === 0 ? (
                      <Typography style={{ fontSize: '12px', color: '#a39285', textAlign: 'center', fontStyle: 'italic', marginTop: '20px' }}>
                        No classes
                      </Typography>
                    ) : (
                      dayEvents.map((evt, idx) => {
                        const flatIndex = entries.findIndex(e => e === evt);
                        return (
                          <Box
                            key={idx}
                            className="event-card"
                            onClick={() => handleOpenDetailModal(evt, flatIndex, dayInfo.fullDateStr)}
                            style={{ cursor: 'pointer' }}
                          >
                            <span className="event-time-badge">
                              ⏰ {formatTimeRange(evt.startTime, evt.endTime)} ({formatDuration(evt.durationMins)})
                            </span>
                            <div className="event-title">{evt.course}</div>
                            <div className="event-student">👤 {evt.studentName}</div>
                            <div className="event-location">📍 {evt.location}</div>

                            <Box className="event-actions">
                              <Tooltip title="Send Schedule to Student via WhatsApp">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleWhatsAppShare({ ...evt, specificDate: dayInfo.fullDateStr }); }} style={{ color: '#25d366', padding: '3px' }}>
                                  <WhatsAppIcon style={{ fontSize: '16px' }} />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenAddModal(dayInfo.dayName, evt, flatIndex); }} style={{ color: '#014486', padding: '3px' }}>
                                  <EditIcon style={{ fontSize: '16px' }} />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Delete">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteEntry(flatIndex); }} style={{ color: '#ef4444', padding: '3px' }}>
                                  <DeleteIcon style={{ fontSize: '16px' }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        );
                      })
                    )}

                    <Button
                      className="add-slot-btn"
                      startIcon={<AddIcon style={{ fontSize: '14px' }} />}
                      onClick={() => handleOpenAddModal(dayInfo.dayName)}
                    >
                      Add Class
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* VIEW 2: MONTHLY CALENDAR VIEW (REPEATS WEEKLY SCHEDULE ACROSS ACADEMIC YEAR) */}
      {viewMode === 'monthly' && (
        <Box className="monthly-calendar-card">
          {/* Month Header Navigation */}
          <Box className="monthly-nav">
            <Box style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <IconButton onClick={handlePrevMonth} size="small" style={{ border: '1px solid #eedfd0', background: '#fffcf9' }}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="h6" style={{ fontWeight: 800, color: '#332822', minWidth: '180px', textAlign: 'center' }}>
                {MONTH_NAMES[currentMonthDate.getMonth()]} {currentMonthDate.getFullYear()}
              </Typography>
              <IconButton onClick={handleNextMonth} size="small" style={{ border: '1px solid #eedfd0', background: '#fffcf9' }}>
                <ChevronRightIcon />
              </IconButton>
              <Button
                size="small"
                variant="outlined"
                onClick={handleTodayMonth}
                style={{ textTransform: 'none', fontWeight: 700, borderColor: '#014486', color: '#014486', borderRadius: '8px' }}
              >
                Today
              </Button>
            </Box>

            <Typography variant="body2" style={{ color: '#78685d', fontWeight: 600 }}>
              📅 Timetable slots repeat automatically every week for the academic year.
            </Typography>
          </Box>

          {/* Days of Week Header */}
          <Box className="monthly-grid" style={{ marginBottom: '8px' }}>
            {DAYS.map(day => (
              <Box key={day} className="monthly-header-day">
                {day.slice(0, 3).toUpperCase()}
              </Box>
            ))}
          </Box>

          {/* Monthly Date Grid */}
          <Box className="monthly-grid">
            {getMonthCalendarCells().map((cell, idx) => (
              <Box
                key={idx}
                className={`monthly-cell ${!cell.isCurrentMonth ? 'other-month' : ''} ${cell.isToday ? 'today' : ''}`}
              >
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <span className="monthly-date-num">{cell.dayNum}</span>
                  {cell.events.length > 0 && cell.isCurrentMonth && (
                    <span className="monthly-event-count-badge">
                      {cell.events.length} {cell.events.length === 1 ? 'class' : 'classes'}
                    </span>
                  )}
                </Box>

                <Box style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, overflow: 'hidden' }}>
                  {cell.events.slice(0, 2).map((evt, eIdx) => {
                    const flatIndex = entries.findIndex(e => e === evt);
                    const timeShort = evt.startTime ? evt.startTime.slice(0, 5) : '';
                    return (
                      <Box
                        key={eIdx}
                        className="monthly-event-pill"
                        onClick={() => handleOpenDetailModal(evt, flatIndex, cell.fullDateStr)}
                        title={`${evt.course} - ${evt.studentName} (${formatTimeRange(evt.startTime, evt.endTime)})`}
                      >
                        {timeShort} {evt.course}
                      </Box>
                    );
                  })}
                  {cell.events.length > 2 && (
                    <div style={{ fontSize: '9px', fontWeight: 800, color: '#014486', textAlign: 'center', marginTop: '1px' }}>
                      +{cell.events.length - 2} more
                    </div>
                  )}
                </Box>

                {cell.isCurrentMonth && (
                  <Button
                    size="small"
                    className="monthly-add-btn"
                    onClick={() => handleOpenAddModal(cell.dayName)}
                  >
                    + Class
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* VIEW 3: TABLE LIST VIEW */}
      {viewMode === 'list' && (
        <Box>
          {/* Week Navigation Header for Table View */}
          <Box className="weekly-nav-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', background: '#ffffff', padding: '12px 18px', borderRadius: '12px', border: '1px solid #eedfd0' }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <IconButton onClick={handlePrevWeek} size="small" style={{ border: '1px solid #eedfd0', background: '#fffcf9' }}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="h6" style={{ fontWeight: 800, color: '#332822', minWidth: '220px', textAlign: 'center', fontSize: '16px' }}>
                {currentMonday.getDate()} {currentMonday.toLocaleDateString('en-US', { month: 'short' })} – {currentSunday.getDate()} {currentSunday.toLocaleDateString('en-US', { month: 'short' })} {currentSunday.getFullYear()}
              </Typography>
              <IconButton onClick={handleNextWeek} size="small" style={{ border: '1px solid #eedfd0', background: '#fffcf9' }}>
                <ChevronRightIcon />
              </IconButton>
              <Button
                size="small"
                variant="outlined"
                onClick={handleTodayWeek}
                style={{ textTransform: 'none', fontWeight: 700, borderColor: '#014486', color: '#014486', borderRadius: '8px' }}
              >
                Today
              </Button>
            </Box>
            <Typography variant="body2" style={{ color: '#78685d', fontWeight: 600 }}>
              🗓️ Showing schedule for current week with exact dates.
            </Typography>
          </Box>

          <Box className="timetable-card">
            {loading ? (
              <Typography style={{ padding: '40px', textAlign: 'center', color: '#78685d' }}>
                Loading timetable...
              </Typography>
            ) : entries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#78685d' }}>
                <SchoolIcon style={{ fontSize: '48px', color: '#eedfd0', marginBottom: '12px' }} />
                <Typography variant="h6" style={{ margin: 0, color: '#332822' }}>No entries in timetable</Typography>
                <Typography variant="body2" style={{ marginTop: '6px' }}>
                  Click <strong>"Add Schedule Entry"</strong> to add your first class slot.
                </Typography>
              </div>
            ) : (
              <table className="timetable-table">
                <thead>
                  <tr>
                    <th>Day & Date</th>
                    <th>Time</th>
                    <th>Duration</th>
                    <th>Student Name</th>
                    <th>Course</th>
                    <th>Location</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const weekDaysWithDates = getWeekDaysWithDates();
                    const dayToDateMap = {};
                    weekDaysWithDates.forEach(d => {
                      dayToDateMap[d.dayName] = d.fullDateStr;
                    });
                    return entries.map((row, idx) => {
                      const rowDateStr = dayToDateMap[row.day] || '';
                      return (
                        <tr
                          key={idx}
                          onClick={() => handleOpenDetailModal(row, idx, rowDateStr)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="day-text">
                            <span style={{ fontWeight: 700 }}>{row.day}</span>
                            {rowDateStr && (
                              <span style={{ fontSize: '12px', color: '#014486', fontWeight: 700, display: 'block', marginTop: '2px' }}>
                                📅 {rowDateStr}
                              </span>
                            )}
                          </td>
                          <td className="time-text">{formatTimeRange(row.startTime, row.endTime)}</td>
                          <td className="duration-text">{formatDuration(row.durationMins)}</td>
                          <td className="student-text">{row.studentName}</td>
                          <td className="course-text">{row.course}</td>
                          <td className="location-text">📍 {row.location || 'Studio A'}</td>
                          <td>
                            <div className="action-row">
                              <Tooltip title="Send Schedule to Student via WhatsApp">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleWhatsAppShare({ ...row, specificDate: rowDateStr }); }} style={{ color: '#25d366' }}>
                                  <WhatsAppIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenAddModal(row.day, row, idx); }} style={{ color: '#014486' }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Delete">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteEntry(idx); }} style={{ color: '#ef4444' }}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            )}
          </Box>
        </Box>
      )}

      {/* POPUP DETAIL VIEW MODAL DIALOG */}
      <Dialog
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ background: '#fbf0e4', borderBottom: '1px solid #eedfd0', color: '#332822', fontWeight: 800, fontSize: '18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>📚 {selectedEntryDetail?.course || 'Class Details'}</span>
          <Chip label={selectedEntryDetail?.day || 'Schedule'} size="small" style={{ background: '#014486', color: '#fff', fontWeight: 700 }} />
        </DialogTitle>

        <DialogContent style={{ padding: '24px', background: '#ffffff' }}>
          {selectedEntryDetail && (
            <Box style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '8px' }}>
              <Box style={{ background: '#faf8f5', padding: '12px 16px', borderRadius: '10px', border: '1px solid #eedfd0' }}>
                <Typography style={{ fontSize: '11px', color: '#78685d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  ⏰ Time & Duration
                </Typography>
                <Typography style={{ fontSize: '15px', fontWeight: 800, color: '#014486', marginTop: '2px' }}>
                  {formatTimeRange(selectedEntryDetail.startTime, selectedEntryDetail.endTime)} ({formatDuration(selectedEntryDetail.durationMins)})
                </Typography>
              </Box>

              <Box style={{ background: '#faf8f5', padding: '12px 16px', borderRadius: '10px', border: '1px solid #eedfd0' }}>
                <Typography style={{ fontSize: '11px', color: '#78685d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  👤 Assigned Student(s)
                </Typography>
                <Typography style={{ fontSize: '14px', fontWeight: 700, color: '#332822', marginTop: '2px' }}>
                  {selectedEntryDetail.studentName || 'All Students'}
                </Typography>
              </Box>

              <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <Box style={{ background: '#faf8f5', padding: '12px 16px', borderRadius: '10px', border: '1px solid #eedfd0' }}>
                  <Typography style={{ fontSize: '11px', color: '#78685d', fontWeight: 700 }}>
                    📍 Location
                  </Typography>
                  <Typography style={{ fontSize: '13px', fontWeight: 700, color: '#332822', marginTop: '2px' }}>
                    {selectedEntryDetail.location || 'Studio A'}
                  </Typography>
                </Box>

                <Box style={{ background: '#faf8f5', padding: '12px 16px', borderRadius: '10px', border: '1px solid #eedfd0' }}>
                  <Typography style={{ fontSize: '11px', color: '#78685d', fontWeight: 700 }}>
                    📅 Scheduled Day / Date
                  </Typography>
                  <Typography style={{ fontSize: '13px', fontWeight: 700, color: '#332822', marginTop: '2px' }}>
                    {selectedEntryDetail.specificDate ? `${selectedEntryDetail.day}, ${selectedEntryDetail.specificDate}` : selectedEntryDetail.day}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions style={{ padding: '16px 24px', background: '#faf8f5', borderTop: '1px solid #eedfd0', flexWrap: 'wrap', gap: '8px' }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<WhatsAppIcon />}
            onClick={() => {
              handleWhatsAppShare(selectedEntryDetail);
              setDetailModalOpen(false);
            }}
            style={{ background: '#25d366', color: '#ffffff', textTransform: 'none', fontWeight: 700 }}
          >
            Send Schedule
          </Button>

          <Button
            size="small"
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              setDetailModalOpen(false);
              handleOpenAddModal(selectedEntryDetail?.day, selectedEntryDetail, selectedEntryDetail?.flatIndex);
            }}
            style={{ background: '#014486', color: '#ffffff', textTransform: 'none', fontWeight: 700 }}
          >
            Edit
          </Button>

          <Button
            size="small"
            onClick={() => setDetailModalOpen(false)}
            style={{ textTransform: 'none', color: '#78685d', fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit Entry Modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle style={{ background: '#014486', color: '#ffffff', fontWeight: 800, fontSize: '18px' }}>
          {editingEntryIndex !== null ? 'Edit Schedule Entry' : 'Add Schedule Entry'}
        </DialogTitle>
        <DialogContent style={{ padding: '24px', background: '#faf8f5' }}>
          <Box style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '10px' }}>
            {/* Days Selection with Quick Presets */}
            <Box>
              <Typography variant="body2" style={{ fontWeight: 700, marginBottom: '6px', color: '#332822' }}>
                Select Day(s) *
              </Typography>
              <Box style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setEntryForm(prev => ({ ...prev, days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"] }))}
                  style={{ textTransform: 'none', fontSize: '11px', fontWeight: 700, borderColor: '#014486', color: '#014486' }}
                >
                  ⚡ All Weekdays (Mon – Fri)
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setEntryForm(prev => ({ ...prev, days: [...DAYS] }))}
                  style={{ textTransform: 'none', fontSize: '11px', fontWeight: 700, borderColor: '#014486', color: '#014486' }}
                >
                  🌟 All 7 Days (Mon – Sun)
                </Button>
              </Box>

              <FormControl size="small" fullWidth>
                <InputLabel>Select Days *</InputLabel>
                <Select
                  multiple
                  value={entryForm.days}
                  label="Select Days *"
                  onChange={(e) => {
                    const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                    setEntryForm(prev => ({ ...prev, days: val }));
                  }}
                  input={<OutlinedInput label="Select Days *" />}
                  renderValue={(selected) => (
                    <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {selected.map((d) => (
                        <Chip key={d} label={d} size="small" color="primary" />
                      ))}
                    </Box>
                  )}
                >
                  {DAYS.map(d => (
                    <MenuItem key={d} value={d}>
                      <Checkbox checked={entryForm.days.indexOf(d) > -1} />
                      <ListItemText primary={d} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <TextField
                label="Start Time *"
                type="time"
                value={entryForm.startTime}
                onChange={(e) => setEntryForm(prev => ({ ...prev, startTime: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
              <TextField
                label="End Time *"
                type="time"
                value={entryForm.endTime}
                onChange={(e) => setEntryForm(prev => ({ ...prev, endTime: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Box>

            <FormControl size="small" fullWidth>
              <InputLabel>Select Class</InputLabel>
              <Select
                value={entryForm.classId}
                label="Select Class"
                onChange={(e) => handleClassChangeInModal(e.target.value)}
              >
                <MenuItem value="">-- Select Class --</MenuItem>
                {classes.map(c => {
                  const cId = c.classId || c.id || c.value;
                  return (
                    <MenuItem key={cId} value={cId}>
                      {formatClassName(c)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Select Student(s)</InputLabel>
              <Select
                multiple
                value={entryForm.studentIds}
                onChange={(e) => {
                  const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                  setEntryForm(prev => ({ ...prev, studentIds: val }));
                }}
                input={<OutlinedInput label="Select Student(s)" />}
                renderValue={(selected) => (
                  <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {(selected || []).map((val) => {
                      const stList = Array.isArray(classStudents) && classStudents.length > 0 ? classStudents : (students || []);
                      const st = stList.find(s => String(s?.userId || s?.id || s?.user?.userId || s?.user?.id) === String(val));
                      const name = st ? getStudentFullName(st) : val;
                      return <Chip key={val} label={name} size="small" />;
                    })}
                  </Box>
                )}
              >
                {(Array.isArray(classStudents) && classStudents.length > 0 ? classStudents : (students || [])).map((s, idx) => {
                  const sId = String(s.userId || s.id || s.user?.userId || s.user?.id || `st-${idx}`);
                  const sName = getStudentFullName(s);
                  const sAdm = getStudentAdmissionNo(s);
                  const label = sAdm ? `${sName} (${sAdm})` : sName;
                  const isChecked = entryForm.studentIds.indexOf(sId) > -1;

                  return (
                    <MenuItem key={sId} value={sId}>
                      <Checkbox checked={isChecked} />
                      <ListItemText primary={label} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <TextField
              label="Course / Subject *"
              placeholder="e.g. Classical Dance, Guitar, Vocals, Tabla"
              value={entryForm.course}
              onChange={(e) => setEntryForm(prev => ({ ...prev, course: e.target.value }))}
              fullWidth
              size="small"
            />

            <TextField
              label="Location / Studio"
              placeholder="e.g. Studio A, Room 102"
              value={entryForm.location}
              onChange={(e) => setEntryForm(prev => ({ ...prev, location: e.target.value }))}
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', background: '#ffffff' }}>
          <Button onClick={() => setModalOpen(false)} style={{ textTransform: 'none', color: '#78685d' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEntry}
            style={{ background: '#014486', color: '#ffffff', textTransform: 'none', fontWeight: 700 }}
          >
            {editingEntryIndex !== null ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeacherSchedules;
